import React from "react";
import { motion } from "framer-motion";

export default function DailySummary() {
  return (
    <section id="summary" data-testid="summary-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">07 — Daily intelligence</div>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium">
            Wake up to one clear signal.
            <br /><span className="gradient-text-teal-lav">Email, WhatsApp, done.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-2xl">
            No dashboard-diving required. A short, sharp brief lands where you already look.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {/* Email preview */}
          <motion.div initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            data-testid="summary-email-card"
            className="rounded-3xl border border-white/[0.08] bg-ink-800/70 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 7 9-7"/></svg>
                Inbox · hello@healthirl.in
              </div>
              <span className="text-[10px] text-zinc-500 font-mono">Tue · 06:45</span>
            </div>
            <div className="p-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">Your daily brief</div>
              <div className="mt-2 font-heading text-2xl tracking-tight">Recovery ok. Watch afternoon glucose.</div>
              <div className="mt-4 grid grid-cols-4 gap-2">
                {[
                  { l: "Health", v: 82, c: "teal-soft" },
                  { l: "Food", v: 74, c: "teal-soft" },
                  { l: "Mood", v: 68, c: "lav-400" },
                  { l: "Sleep", v: 63, c: "coral-400" },
                ].map((s) => (
                  <div key={s.l} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 text-center">
                    <div className={`font-heading text-lg tracking-tight text-${s.c}`}>{s.v}</div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{s.l}</div>
                  </div>
                ))}
              </div>
              <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                <li className="flex gap-2"><span className="text-teal-soft">•</span>25g protein at dinner (deficit from Sun–Mon)</li>
                <li className="flex gap-2"><span className="text-teal-soft">•</span>Walk 15 min after lunch — blunt today&rsquo;s spike</li>
                <li className="flex gap-2"><span className="text-lav-400">•</span>Wind-down at 22:15 · phone out of room</li>
              </ul>
            </div>
          </motion.div>

          {/* WhatsApp preview */}
          <motion.div initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="summary-whatsapp-card"
            className="rounded-3xl border border-white/[0.08] bg-ink-800/70 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M20.5 3.5A11.9 11.9 0 0 0 12 0a12 12 0 0 0-10.4 18l-1.5 6 6.1-1.6A12 12 0 0 0 12 24a12 12 0 0 0 8.5-20.5zM12 21.8a9.8 9.8 0 0 1-5-1.4l-.4-.2-3.6 1 1-3.5-.2-.4A9.8 9.8 0 1 1 21.8 12 9.8 9.8 0 0 1 12 21.8z"/></svg>
                WhatsApp · +91 healthIRL
              </div>
              <span className="text-[10px] text-zinc-500 font-mono">Tue · 16:12</span>
            </div>
            <div className="p-6 space-y-3">
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/[0.05] border border-white/[0.08] px-4 py-2.5 text-sm">
                Hi Mansi 👋 Quick alert — glucose is trending high right now (154 mg/dL, up from 108).
              </div>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/[0.05] border border-white/[0.08] px-4 py-2.5 text-sm">
                Likely cause: today&rsquo;s lunch + short night. A 12-min walk in the next 20 minutes usually resolves this for you.
              </div>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-teal-mid/[0.10] border border-teal-mid/25 px-4 py-2.5 text-sm">
                <span className="text-teal-soft text-[10px] uppercase tracking-wider">Action</span>
                <div className="mt-1">Reply <span className="text-white">DONE</span> once you&rsquo;re back — I&rsquo;ll adjust tonight&rsquo;s plan.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
